import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Role constants
export const ROLE_SUPER_ADMIN = 'super_admin';
export const ROLE_ORG_ADMIN = 'org_admin';
export const ROLE_ORG_USER = 'org_user';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      authAPI.getMe()
        .then((res) => {
          setUser(res.data);
          localStorage.setItem('user', JSON.stringify(res.data));
        })
        .catch(() => {
          logout();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const deviceToken = localStorage.getItem('device_token');
    const response = await authAPI.login({ email, password, device_token: deviceToken || undefined });
    const data = response.data;
    
    // 2FA required — return the session info without setting auth state
    if (data.two_fa_required) {
      return { twoFARequired: true, sessionId: data.session_id, emailHint: data.email_hint };
    }
    
    const { access_token, user: userData } = data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const register = async (formData) => {
    const response = await authAPI.register(formData);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const complete2FA = async (sessionId, code, rememberDevice = false) => {
    const response = await authAPI.verify2FA({ session_id: sessionId, code, remember_device: rememberDevice });
    const { access_token, user: userData, device_token } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    if (device_token) {
      localStorage.setItem('device_token', device_token);
    }
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Keep device_token across logouts — it's the "remember this device" token
    setUser(null);
  };

  const isSuperAdmin = user?.role === ROLE_SUPER_ADMIN;
  const isOrgAdmin = user?.role === ROLE_ORG_ADMIN;
  const isAdmin = isSuperAdmin || isOrgAdmin;
  const organizationId = user?.organization_id;
  const organizationName = user?.organization_name;
  const orgFeatures = user?.org_features || {};

  const hasFeature = (featureName) => {
    if (isSuperAdmin) return true;
    return !!orgFeatures[featureName];
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login,
      register,
      complete2FA,
      logout, 
      loading, 
      isSuperAdmin,
      isOrgAdmin,
      isAdmin,
      organizationId,
      organizationName,
      orgFeatures,
      hasFeature
    }}>
      {children}
    </AuthContext.Provider>
  );
};
